import React, { useEffect, useState } from "react"
import Button from "../components/Button"
import AuthCard from "../components/AuthCard"
import requests from "../api/requests"
import { errorSnackBar } from "../components/snackBarOptions"
import { useSnackbar } from "react-simple-snackbar"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { logout } from "../app/features/userSlice"
import {
  fetchSubscriptionStatus,
  selectHasAccess,
  selectPending,
  selectProvider,
  selectSubscriptionRawStatus,
  selectTrial,
  routeForStatus,
} from "../app/features/subscriptionStatusSlice"
import { openPendingPayment, pendingActionLabel, trialEndedNotice } from "../components/pendingPayment"
import { Skeleton } from "@mui/material"

function UnpaidScreen() {
  const [isLoading, setIsLoading] = useState(false)
  const [openErrorSnackbar] = useSnackbar(errorSnackBar)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const status = useSelector(selectSubscriptionRawStatus)
  const provider = useSelector(selectProvider)
  const pending = useSelector(selectPending)
  const hasAccess = useSelector(selectHasAccess)
  const trial = useSelector(selectTrial)
  const loading = useSelector((s) => s.subscriptionStatus.loading)
  const fetched = useSelector((s) => s.subscriptionStatus.lastFetchedAt > 0)
  // Redirects wait for the status fetched on mount: the cached one can be
  // minutes old (e.g. still has_access right after the grace ended).
  const [fresh, setFresh] = useState(false)

  useEffect(() => {
    dispatch(fetchSubscriptionStatus()).finally(() => setFresh(true))
  }, [dispatch])

  useEffect(() => {
    if (!fresh) return
    const route = hasAccess ? "/" : routeForStatus(status)
    if (route !== "/unpaid") navigate(route)
  }, [fresh, hasAccess, status, navigate])

  const loadPortal = async () => {
    let resp = null
    try {
      setIsLoading(true)
      resp = await requests.createPortalSession({
        returnURL: window.location.origin,
      })
    } catch (error) {
      openErrorSnackbar("Erro ao acessar o portal de pagamento")
    } finally {
      setIsLoading(false)
    }

    if (resp?.data?.url) {
      window.location.href = resp.data.url
    }
  }

  const payPending = async () => {
    setIsLoading(true)
    try {
      await openPendingPayment(pending, navigate)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await requests.logout()
    } catch {
      // ignore
    }
    dispatch(logout())
  }

  const logoutLink = (
    <p className="unpaid__logout-link" onClick={handleLogout}>
      Sair da conta
    </p>
  )

  if (loading && !fetched) {
    return (
      <AuthCard>
        <Skeleton variant="text" width="60%" height={40} style={{ marginBottom: "16px" }} />
        <Skeleton variant="text" width="100%" height={20} style={{ marginBottom: "8px" }} />
        <Skeleton variant="text" width="90%" height={20} style={{ marginBottom: "8px" }} />
        <Skeleton variant="text" width="80%" height={20} style={{ marginBottom: "24px" }} />
        <Skeleton variant="rectangular" width="100%" height={46} style={{ borderRadius: "8px" }} />
      </AuthCard>
    )
  }

  // past_due out of grace and unpaid: pay the pending charge (Pix QR, card
  // in the portal, hosted invoice); without one, Stripe members fix their
  // payment method in the portal and Pix members go to their subscription.
  const cta = pending
    ? { text: pendingActionLabel(pending.kind), onClick: payPending }
    : provider === "asaas"
    ? { text: "Ver minha assinatura", onClick: () => navigate("/subscription") }
    : { text: "Atualizar pagamento", onClick: loadPortal }

  // A Pix trial that ended unpaid (copy.md T13).
  const copy =
    trial?.state === "ended"
      ? trialEndedNotice(trial)
      : {
          title: "Pagamento pendente",
          body: "Ainda não recebemos o pagamento da sua assinatura. Assim que ele chegar, seu acesso volta na hora.",
        }

  return (
    <AuthCard>
      <h1>{copy.title}</h1>
      <h2>{copy.body}</h2>
      <Button onClick={cta.onClick} text={cta.text} isLoading={isLoading} />
      {logoutLink}
    </AuthCard>
  )
}

export default UnpaidScreen
